// POROS · READINESS — CORE of REGULATE. Resource readiness for the orb's CURRENT Step.
// POROS is the display name over the NUMA tool (olmps/moyriSeats.ts), so toolId = 'numa'.
//
// Minimal adapter from existing data; the requirement list lives in ONE function
// (`porosRequirements`) so deeper Poros logic (people / product / money) can replace it
// later without touching the model builder below. Today only the classes real data
// supports are produced: Parameters (time slot, energy) and Process (earlier TKNE steps).
// It reuses the same checks the orb's Step "R" row makes (v10/orb/stepRows.tsx).
import { eventsOn } from '../../time';
import { slotState } from '../../shared';
import type { OracleProject } from '../../../../olmps/useOracleProjects';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel } from './util';

export type PorosKind = 'Place' | 'Process' | 'Parameters' | 'People' | 'Product';
export interface PorosRequirement {
  name: string;
  ok: boolean;
  kind: PorosKind;
}

const BASE = { id: 'poros', toolId: 'poros', orderFunction: 'regulate', title: 'READINESS', deityName: 'POROS', agentId: 'numa' } as const;
const SOON_MS = 15 * 60_000;

const trunc = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** What the current Step needs, and whether each need is met. Empty when the Step is already done. */
export function porosRequirements(c: WidgetCtx): PorosRequirement[] {
  const step = c.step;
  if (!step || step.completed) return [];
  const out: PorosRequirement[] = [];

  // Time slot (Parameters): a ghost step with no task isn't on a day's plan yet; otherwise it needs a
  // slot that doesn't clash with a KRNOS event — the same test the orb's Step R row uses.
  if (step.source === 'step' && !step.taskId) {
    out.push({ name: 'Place on the plan', ok: false, kind: 'Parameters' });
  } else {
    const s = slotState(step, eventsOn(c.today.calendarEvents, step.date));
    out.push({ name: s.ready ? 'Time slot' : s.why === 'no time slot' ? 'Time slot' : `Time slot (${s.why})`, ok: s.ready, kind: 'Parameters' });
  }

  // Earlier TKNE steps (Process): prior steps of the same project that aren't complete.
  if (step.designId && step.stageKey) {
    const project = (Object.values(c.projects).flat() as OracleProject[]).find((p) => p.id === step.designId);
    const at = project ? project.steps.findIndex((s) => s.id === step.stageKey) : -1;
    if (project && at > 0) {
      for (const p of project.steps.slice(0, at)) {
        out.push({ name: `Before: ${trunc(p.text, 24)}`, ok: p.status === 'C', kind: 'Process' });
      }
    }
  }

  // Energy (Parameters): a missing 'charge' resource when the charge sits below the green band.
  if (c.today.flo) out.push({ name: 'Charge', ok: !c.today.chargeBelowGreen, kind: 'Parameters' });

  return out;
}

export function usePorosWidget(c: WidgetCtx): OrbWidgetModel {
  const none = 'No resource requirements set';
  if (!c.step) return emptyModel(BASE, c, none);
  if (c.step.completed) return emptyModel(BASE, c, 'Step done');

  const reqs = porosRequirements(c);
  if (!reqs.length) return emptyModel(BASE, c, none);

  const missing = reqs.filter((r) => !r.ok);
  // Biggest gap: the first missing need in order — slot, then earlier steps, then charge.
  const gap = missing[0] ?? null;
  const ready = missing.length === 0;

  const start = c.step.start;
  const due = !!start && start.getTime() - c.now.getTime() <= SOON_MS; // already passed, or within 15 min today
  const critical = !ready && due;
  const status: OrbWidgetModel['status'] = ready ? 'active' : critical ? 'critical' : 'attention';

  const lines = [
    ...reqs.map((r) => `${r.ok ? '✓' : '✗'} ${r.kind}: ${r.name}`),
    gap ? `Missing: ${gap.name}` : 'Missing: nothing',
    `Status: ${ready ? 'READY' : 'NOT READY'}`,
  ];

  return {
    ...BASE,
    status,
    summary: ready ? 'Ready' : missing.length === 1 ? '1 resource gap' : `${missing.length} resource gaps`,
    metrics: [
      { label: 'Required', value: reqs.length },
      { label: 'Available', value: reqs.length - missing.length, tone: 'positive' },
      { label: 'Missing', value: missing.length, tone: ready ? 'neutral' : critical ? 'critical' : 'warning' },
    ],
    alert: ready ? null : gap ? `Missing: ${gap.name}` : null,
    lines,
    primaryAction: {
      label: 'Resolve gap',
      run: () => c.openTool('kyrs', c.step?.taskId ? { initialTaskId: c.step.taskId } : undefined),
      tone: ready ? 'ghost' : 'gold',
    },
    signals: { active: true, alerted: !ready, related: true, blocker: critical },
  };
}
